import { GridRowSelectionModel } from "@mui/x-data-grid";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import CommonAlert from "@/components/CommonAlert";
import { QUERY_KEYS } from "@/constants";
import { useChangePassengerStatusMutation } from "@/requests/transport.mutations";
import { IPlanPassenger } from "@/types/tasks.types";

import { ShowStatusConfirmation } from "..";

function WelcomeTeamUpdateDialog({
    showStatusConfirmation,
    handleStatusConfirmation,
    selectionList,
    onDetailRowSelectionModelChange,
    items,
}: {
    selectionList: GridRowSelectionModel;
    showStatusConfirmation: ShowStatusConfirmation;
    handleStatusConfirmation: (val?: ShowStatusConfirmation) => void;
    onDetailRowSelectionModelChange?: (items: GridRowSelectionModel) => void;
    items: IPlanPassenger<"api">[];
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { mutateAsync, isPending } = useChangePassengerStatusMutation();

    const passengers = useMemo(() => {
        if (!showStatusConfirmation) return [];
        const [current] = showStatusConfirmation;
        return items.reduce<IPlanPassenger<"api">[]>((prev, curr) => {
            if (curr.status === current && selectionList.includes(curr.id)) prev.push(curr);

            return prev;
        }, []);
    }, [showStatusConfirmation, selectionList, items]);

    const handleConfirm = async () => {
        if (isPending || !showStatusConfirmation) return;
        try {
            const passengerIds = passengers.map((passenger) => passenger.id);
            const responses = await mutateAsync({
                passengerIds,
                status: showStatusConfirmation[1],
            });
            const completedList: string[] = [];
            for (const [index, response] of responses.entries()) {
                setTimeout(() => {
                    if (!response.ok) Sentry.captureException(new Error(response.message));
                    else completedList.push(response.id);
                    enqueueSnackbar(response.message || "", {
                        variant: response.ok ? "success" : "error",
                    });

                    if (index === responses.length - 1 && onDetailRowSelectionModelChange) {
                        onDetailRowSelectionModelChange(
                            selectionList.filter((item) => !completedList.includes(item as string))
                        );
                        if (completedList.length)
                            queryClient.invalidateQueries({
                                queryKey: [QUERY_KEYS.GET_ARRIVALS],
                            });
                    }
                }, index * 50);
            }
            handleStatusConfirmation(null);
        } catch (error) {
            console.error("Registration error:", error);
        }
    };

    return (
        <CommonAlert
            open={!!showStatusConfirmation}
            loading={isPending}
            description="confirmations.updatePassengerStatus"
            color="primary"
            onClose={() => handleStatusConfirmation()}
            handleConfirm={handleConfirm}
            html
            descriptionOpts={{
                count: passengers?.length || 0,
                prev: t(`passengerStatuses.${passengers[0]?.status}`),
                next: t(`passengerStatuses.${showStatusConfirmation && showStatusConfirmation[1]}`),
            }}
        />
    );
}

export default WelcomeTeamUpdateDialog;
